/* Ayudate · Dashboard del tutor
   Ruta protegida: sin un token de sesión válido no hay nada que mostrar
   acá, así que todo el componente gira alrededor de cargar()/validar
   contra obtenerDashboardTutor(), y de mandar a /login apenas el
   servidor dice que la sesión no sirve (ausente, vencida o falsificada:
   el mismo camino para los tres, porque a la persona le da igual por qué
   — quiere volver a entrar). */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Marca } from "../componentes/Marca";
import { ESTILOS_MINIMOS } from "../estilos/minimos";
import { api, ErrorApi } from "../api";
import { irA_ruta } from "../lib/rutas";
import { leerSesionTutor, borrarSesionTutor } from "../lib/sesionTutor";
import { Capa } from "../componentes/Capa";
import { CampoPassword } from "../componentes/CampoPassword";
import { Grilla } from "../componentes/Grilla";
import { AlertaArchivo } from "../componentes/Mensajes";
import { ProximaSesion } from "../componentes/ProximaSesion";
import { CancelarSesion } from "../componentes/CancelarSesion";
import { RechazarSolicitud } from "../componentes/RechazarSolicitud";
import { Accesibilidad, FiltrosColor } from "../componentes/Accesibilidad";
import { cargarA11y, guardarA11y, clasesA11y } from "../lib/a11y";
import { FRANJAS, franjasDe, escalaDeInstitucion, esDeOpciones, fueraDeEscala,
         pideRespaldo, soloDigitos, valorDeEscalaOk } from "../lib/horarios";
import { prepararFoto } from "../lib/foto";
import { prepararComprobante } from "../lib/comprobante";
import { validarExtensionImagen, FORMATOS_IMAGEN_TEXTO } from "../lib/tiposArchivo";
import { AREAS, INSTITUCIONES } from "../datos/areas";
import { precioDe, TARIFA_MINIMA, TARIFA_SUGERIDA } from "../lib/tarifas";
import { colones, nombreCurso } from "../lib/formato";

const CODIGOS_SESION_MUERTA = ["SESION_INVALIDA", "SESION_VENCIDA"];

function fechaHora(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const fecha = d.toLocaleDateString("es-CR", { day: "numeric", month: "long" });
  const hora = d.toLocaleTimeString("es-CR", { hour: "numeric", minute: "2-digit" });
  return `${fecha} · ${hora}`;
}

export function PaginaDashboardTutor() {
  const [sesion] = useState(() => leerSesionTutor());
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [datos, setDatos] = useState(null);
  const [editando, setEditando] = useState(null);   // curso en edición, o null
  const [actualizandoCurso, setActualizandoCurso] = useState(null);   // { curso, accion } | null
  const [editandoHorario, setEditandoHorario] = useState(false);
  const [agregandoCurso, setAgregandoCurso] = useState(false);
  const [cambiandoPassword, setCambiandoPassword] = useState(false);
  const [cambiandoCorreo, setCambiandoCorreo] = useState(false);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [paginaHistorial, setPaginaHistorial] = useState(1);
  const [cancelando, setCancelando] = useState(null);   // la reunión que se está cancelando
  const [rechazando, setRechazando] = useState(null);   // la solicitud que se está rechazando
  const [paginaResenas, setPaginaResenas] = useState(1);
  const [viendoResenasCurso, setViendoResenasCurso] = useState(null);   // código de curso, o null
  const [a11y, setA11y] = useState(cargarA11y);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [errorFoto, setErrorFoto] = useState("");
  const [respondiendoSolicitud, setRespondiendoSolicitud] = useState(null);   // token en proceso, o null
  const [exito, setExito] = useState("");
  const [formIncidente, setFormIncidente] = useState({ curso: "", estudiante: "", tipo: "queja", descripcion: "" });
  const [creandoIncidente, setCreandoIncidente] = useState(false);
  const [errorIncidente, setErrorIncidente] = useState("");
  const fotoTokenRef = useRef(0);
  const exitoTimeoutRef = useRef(null);

  const irALogin = useCallback((mensaje) => {
    borrarSesionTutor();
    irA_ruta("/login" + (mensaje ? `?msg=${encodeURIComponent(mensaje)}` : ""));
  }, []);

  const mostrarExito = useCallback((mensaje) => {
    if (exitoTimeoutRef.current) clearTimeout(exitoTimeoutRef.current);
    setExito(mensaje);
    exitoTimeoutRef.current = setTimeout(() => setExito(""), 4000);
  }, []);

  const cargar = useCallback(async () => {
    if (!sesion) return;
    setCargando(true); setError("");
    try {
      const r = await api.obtenerDashboardTutor(sesion.token);
      setDatos(r);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) {
        irALogin("Su sesión venció. Inicie sesión de nuevo.");
        return;
      }
      setError(err.message || "No pudimos cargar su dashboard. Pruebe de nuevo.");
    } finally {
      setCargando(false);
    }
  }, [sesion, irALogin]);

  useEffect(() => {
    if (!sesion) { irALogin(); return; }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { guardarA11y(a11y); }, [a11y]);

  const salir = () => irALogin();

  const cambiarPublicado = async (curso, publicado) => {
    if (actualizandoCurso) return;
    setActualizandoCurso({ curso, accion: "publicado" });
    setError("");
    try {
      await api.cambiarPublicadoCurso(sesion.token, curso, publicado);
      await cargar();
      mostrarExito(publicado === "SI" ? "Curso publicado." : "Curso oculto.");
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return irALogin("Su sesión venció. Inicie sesión de nuevo.");
      setError(err.message || "No pudimos actualizar el curso.");
    } finally {
      setActualizandoCurso(null);
    }
  };

  const cambiarEstado = async (curso, nuevoEstado) => {
    if (actualizandoCurso) return;
    if (nuevoEstado === "retirado" && !window.confirm(
      `¿Retirar el curso ${curso}? Se va a ocultar de inmediato y no va a poder recibir más `
      + "solicitudes para él hasta que lo reactive. Le vamos a mandar un correo de confirmación."
    )) return;
    setActualizandoCurso({ curso, accion: "estado" });
    setError("");
    try {
      await api.cambiarEstadoCurso(sesion.token, curso, nuevoEstado);
      await cargar();
      mostrarExito(nuevoEstado === "retirado" ? "Curso retirado." : "Curso reactivado.");
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return irALogin("Su sesión venció. Inicie sesión de nuevo.");
      setError(err.message || "No pudimos actualizar el curso.");
    } finally {
      setActualizandoCurso(null);
    }
  };

  /* Mismo camino único de escritura que el botón del correo (Confirmar/No
     puedo): esto solo cambia dónde se toca — ver responderSolicitudTutor
     en apps-script.gs, que replica exactamente ejecutarAccionSolicitudTutor.
     Solo confirma: rechazar pasa por el modal de acá abajo, porque pide el
     mensaje que va a leer el estudiante. */
  const confirmarSolicitud = async (solicitud) => {
    if (respondiendoSolicitud) return;
    setRespondiendoSolicitud(solicitud.token);
    setError("");
    try {
      await api.responderSolicitudTutor(sesion.token, solicitud.token, "confirmar");
      await cargar();
      mostrarExito("Confirmado. Le avisamos al estudiante para que haga el pago.");
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return irALogin("Su sesión venció. Inicie sesión de nuevo.");
      setError(err.message || "No pudimos procesar la solicitud.");
    } finally {
      setRespondiendoSolicitud(null);
    }
  };

  /* Igual que cancelarSesion: el modal arma el formulario y traduce sus
     errores, y acá se queda lo de siempre —la sesión viva y a dónde mandar
     si venció. El mensaje es obligatorio y el servidor lo revalida. */
  const rechazarSolicitud = async (mensaje) => {
    try {
      await api.responderSolicitudTutor(sesion.token, rechazando.token, "rechazar", mensaje);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return irALogin("Su sesión venció. Inicie sesión de nuevo.");
      throw err;
    }
  };

  const elegirFoto = async (archivo) => {
    try {
      validarExtensionImagen(archivo);
    } catch (err) {
      setErrorFoto(err.message);
      return;
    }
    const miToken = ++fotoTokenRef.current;
    setSubiendoFoto(true); setErrorFoto("");
    try {
      const dataUrl = await prepararFoto(archivo);
      if (fotoTokenRef.current !== miToken) return;   // se eligió otra mientras tanto
      await api.cambiarFotoTutor(sesion.token, dataUrl);
      if (fotoTokenRef.current !== miToken) return;
      await cargar();
      mostrarExito("Foto de perfil actualizada.");
    } catch (err) {
      if (fotoTokenRef.current !== miToken) return;
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return irALogin("Su sesión venció. Inicie sesión de nuevo.");
      setErrorFoto(err.message || "No pudimos actualizar su foto.");
    } finally {
      if (fotoTokenRef.current === miToken) setSubiendoFoto(false);
    }
  };

  const reportarIncidente = async () => {
    if (!formIncidente.descripcion.trim()) {
      setErrorIncidente("Describa brevemente qué pasó.");
      return;
    }
    setCreandoIncidente(true);
    setErrorIncidente("");
    try {
      await api.crearIncidenteTutor(sesion.token, {
        curso: formIncidente.curso,
        estudiante: formIncidente.estudiante.trim(),
        tipoIncidente: formIncidente.tipo,
        descripcion: formIncidente.descripcion.trim(),
      });
      setFormIncidente({ curso: "", estudiante: "", tipo: "queja", descripcion: "" });
      mostrarExito("Incidente reportado. El equipo le va a dar seguimiento.");
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return irALogin("Su sesión venció. Inicie sesión de nuevo.");
      setErrorIncidente(err.message || "No pudimos guardar el incidente.");
    } finally {
      setCreandoIncidente(false);
    }
  };

  /* El modal solo arma el formulario; quién es el dueño de la sesión viva y
     qué hacer si venció se queda acá, igual que en el resto del panel. Los
     demás errores se relanzan para que el modal los traduzca y los muestre
     sin cerrarse, que es donde la persona los puede leer. */
  const cancelarSesion = async (motivo) => {
    try {
      await api.cancelarSesionTutor(sesion.token, cancelando.token, motivo);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return irALogin("Su sesión venció. Inicie sesión de nuevo.");
      throw err;
    }
  };

  if (!sesion) return null;   // irALogin() ya está redirigiendo

  /* Historial de reuniones paginado: 8 por página para no dejarlo scrollear
     al infinito apenas un tutor acumula varias decenas de sesiones. Se
     recalcula en cada render a partir de historialReuniones (ya viene
     ordenado del más reciente al más viejo, ver obtenerDashboardTutor en
     apps-script.gs), sin pedirle nada nuevo al servidor. */
  const POR_PAGINA_HISTORIAL = 8;
  const historial = datos?.historialReuniones || [];
  const paginasHistorial = Math.max(1, Math.ceil(historial.length / POR_PAGINA_HISTORIAL));
  const paginaHistorialSegura = Math.min(paginaHistorial, paginasHistorial);
  const historialVisible = historial.slice(
    (paginaHistorialSegura - 1) * POR_PAGINA_HISTORIAL,
    paginaHistorialSegura * POR_PAGINA_HISTORIAL
  );

  /* Reseñas de estudiantes paginadas: 10 por página. */
  const POR_PAGINA_RESENAS = 10;
  const resenas = datos?.resenas || [];
  const paginasResenas = Math.max(1, Math.ceil(resenas.length / POR_PAGINA_RESENAS));
  const paginaResenasSegura = Math.min(paginaResenas, paginasResenas);
  const resenasVisibles = resenas.slice(
    (paginaResenasSegura - 1) * POR_PAGINA_RESENAS,
    paginaResenasSegura * POR_PAGINA_RESENAS
  );

  return (
    <div className={`v2 ${clasesA11y(a11y)}`}>
      <style>{ESTILOS_MINIMOS}</style>
      <FiltrosColor />

      <header className="dash-topbar">
        <div className="dash-topbar-in">
          <span className="logo"><Marca /></span>
          <div className="dash-topbar-user">
            {datos?.nombre && <span>Hola, {datos.nombre}</span>}
            <button className="btn" onClick={salir}>Cerrar sesión</button>
          </div>
        </div>
      </header>

      <div className="mini-wrap dash-wrap">
        {cargando ? (
          <div className="mini-card"><p style={{ margin: 0 }}>Cargando su dashboard…</p></div>
        ) : !datos ? (
          <div className="mini-card">
            <h1>No pudimos cargar su dashboard</h1>
            {error && <div className="error" role="alert">{error}</div>}
            <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }} onClick={cargar}>
              Reintentar
            </button>
          </div>
        ) : (
          <>
            {error && <div className="error" role="alert" style={{ marginBottom: 20 }}>{error}</div>}
            {exito && <div className="exito" role="status" style={{ marginBottom: 20 }}>{exito}</div>}

            {!!datos.proximasReuniones.length && (
              <ProximaSesion
                sesion={datos.proximasReuniones[0]}
                nombreCurso={nombreCurso(datos.proximasReuniones[0].curso)}
                textoFecha={fechaHora(datos.proximasReuniones[0].inicio)}
              />
            )}

            {(() => {
              const totalSesiones = datos.cursos.reduce((s, c) => s + (c.sesionesCompletadas || 0), 0);
              const totalResenas = datos.cursos.reduce((s, c) => s + (c.resenas || 0), 0);
              const sumaCalificaciones = datos.cursos
                .reduce((s, c) => s + (c.rating || 0) * (c.resenas || 0), 0);
              const ratingPromedio = totalResenas > 0 ? sumaCalificaciones / totalResenas : null;
              /* gananciasEstimadas y pendienteDePago los calcula el servidor
                 (calcularGananciasTutor_ en apps-script.gs), agrupando por el
                 mismo ciclo viernes-jueves que usa el panel admin: acá no se
                 recalculan a partir de sesionesCompletadas (un total de toda
                 la vida) porque eso no permite separar "esta semana" de "ya
                 cerrado y sin pagar". */
              const gananciasEstimadas = datos.gananciasEstimadas || 0;
              const pendienteDePago = datos.pendienteDePago || 0;
              return (
                <div className="stats-row">
                  <div className="stat-card">
                    <span className="stat-num">{totalSesiones}</span>
                    <span className="stat-lbl">Sesiones completadas</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-num">{ratingPromedio != null ? ratingPromedio.toFixed(1) : "—"}</span>
                    <span className="stat-lbl">
                      Calificación promedio{totalResenas ? ` · ${totalResenas} reseña${totalResenas === 1 ? "" : "s"}` : ""}
                    </span>
                    {ratingPromedio == null && <span className="stat-sub">Sin reseñas aún</span>}
                  </div>
                  <div className="stat-card">
                    <span className="stat-num">{colones(gananciasEstimadas)}</span>
                    <span className="stat-lbl">Ganancias estimadas</span>
                    <span className="stat-sub">Ciclo actual: viernes a jueves</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-num">{colones(pendienteDePago)}</span>
                    <span className="stat-lbl">Pendiente de pago</span>
                    {pendienteDePago > 0 && (
                      <span className="stat-sub">De ciclos ya cerrados, a la espera del depósito</span>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="mini-card dash-sec">
              <h2>Mi perfil</h2>
              <div className="perfil-filas">
                <div className="perfil-unificado">
                  <div className="perfil-datos">
                    <div className="perfil-dato">
                      <span className="perfil-dato-lbl">Carrera</span>
                      <span className="perfil-dato-val">{datos.carrera || "—"}</span>
                    </div>
                    <div className="perfil-dato">
                      <span className="perfil-dato-lbl">Año/Nivel</span>
                      <span className="perfil-dato-val">{datos.anio || "—"}</span>
                    </div>
                    <div className="perfil-dato">
                      <span className="perfil-dato-lbl">Email</span>
                      <span className="perfil-dato-val">{datos.correo || "—"}</span>
                    </div>
                  </div>
                  <div className="perfil-foto">
                    <div className="fotoprev">
                      {datos.foto ? <img src={datos.foto} alt="Su foto de perfil" /> : (
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <circle cx="12" cy="8.6" r="3.9" fill="currentColor" />
                          <path d="M4.4 20.4c0-3.9 3.4-6.5 7.6-6.5s7.6 2.6 7.6 6.5" fill="currentColor" />
                        </svg>
                      )}
                    </div>
                    <div className="fotoacc">
                      <AlertaArchivo mensaje={errorFoto} />
                      <label className="lb" style={{ margin: "0 0 10px" }}>Foto de perfil</label>
                      <label className={`btn fotobtn ${subiendoFoto ? "ocupado" : ""}`}>
                        {subiendoFoto ? "Subiendo…" : datos.foto ? "Cambiar foto" : "Subir una foto"}
                        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (f) elegirFoto(f);
                        }} />
                      </label>
                      <p className="ayuda" style={{ margin: "8px 0 0" }}>
                        <b>Cualquier tamaño sirve</b>: la recortamos y la comprimimos acá mismo. Formatos
                        permitidos: <b>{FORMATOS_IMAGEN_TEXTO}</b>.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="perfil-lateral">
                  <div className="perfil-panel">
                    <label className="lb" style={{ margin: 0 }}>Disponibilidad</label>
                    <p style={{ margin: 0 }}>
                      {(() => {
                        const franjas = franjasDe(datos.disponibilidad || {});
                        if (!franjas.length) return "Todavía no marcó ningún horario.";
                        return franjas.map((id) => FRANJAS.find((f) => f.id === id)?.label || id).join(" · ");
                      })()}
                    </p>
                    <button className="btn" onClick={() => setEditandoHorario(true)}>
                      Modificar horario de disponibilidad
                    </button>
                  </div>

                  <div className="perfil-panel">
                    <label className="lb" style={{ margin: 0 }}>Seguridad</label>
                    <button className="btn" onClick={() => setCambiandoPassword(true)}>
                      Cambiar contraseña
                    </button>
                    <button className="btn" onClick={() => setCambiandoCorreo(true)}>
                      Cambiar correo
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mini-card dash-sec">
              <div className="displb">
                <h2 style={{ margin: 0 }}>Mis cursos</h2>
                <button className="btn" onClick={() => setAgregandoCurso(true)}>
                  Agregar nuevo curso
                </button>
              </div>
              {!datos.cursos.length ? (
                <p className="vacio">Todavía no tiene cursos cargados.</p>
              ) : (
                <div className="cursos-grid">
                  {datos.cursos.map((c) => {
                    const ocupado = actualizandoCurso?.curso === c.curso;
                    return (
                      <div className="curso-card" key={c.curso}>
                        <div className="curso-card-titulo">
                          <b>{nombreCurso(c.curso)}</b>
                          {c.estado === "aprobado" ? (
                            <span className={`chip ${c.publicado === "SI" ? "on" : "off"}`}>
                              {c.publicado === "SI" ? "Publicado" : "Oculto"}
                            </span>
                          ) : c.estado === "retirado" ? (
                            <span className="chip off">Retirado</span>
                          ) : c.estado === "rechazado" ? (
                            <span className="chip danger">No aprobado</span>
                          ) : (
                            <span className="chip warn">{c.estado === "pendiente" ? "En revisión" : (c.estado || "En revisión")}</span>
                          )}
                        </div>
                        <p className="curso-card-tarifa">
                          Tarifa: {colones(precioDe(c.tarifaBase).cliente)}/h ·
                          Recibe: {colones(precioDe(c.tarifaBase).tutor)}/h
                        </p>
                        <button type="button" className="btn-ghost"
                          style={{ display: "block", marginTop: 10 }}
                          onClick={() => setViendoResenasCurso(c.curso)}>
                          Ver reseñas{c.resenas ? ` (${c.resenas})` : ""}
                        </button>
                        <p className="curso-card-desc">{c.descripcion || "Sin descripción todavía."}</p>

                        <div className="curso-card-acciones">
                          <div className="curso-card-acciones-sec">
                            <button className="btn-ghost" disabled={ocupado} onClick={() => setEditando(c)}>
                              Editar
                            </button>
                            {c.estado === "aprobado" && (
                              <button className="btn-ghost" disabled={ocupado}
                                onClick={() => cambiarPublicado(c.curso, c.publicado === "SI" ? "NO" : "SI")}>
                                {c.publicado === "SI" ? "Ocultar" : "Publicar"}
                              </button>
                            )}
                          </div>
                          {c.estado === "aprobado" && (
                            <button className="btn-peligro-ghost" disabled={ocupado}
                              title="Va a dejar de recibir solicitudes de este curso hasta que lo reactive."
                              onClick={() => cambiarEstado(c.curso, "retirado")}>
                              Retirar curso
                            </button>
                          )}
                          {c.estado === "retirado" && (
                            <button className="btn btn-p" disabled={ocupado}
                              onClick={() => cambiarEstado(c.curso, "aprobado")}>
                              Reactivar Curso
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {!!datos.solicitudesPendientes?.length && (
              <div className="mini-card dash-sec">
                <h2>Solicitudes por responder</h2>
                {datos.solicitudesPendientes.map((s) => {
                  const ocupado = respondiendoSolicitud === s.token;
                  return (
                    <div className="fila" key={s.token}>
                      <div className="fila-info">
                        <b>{nombreCurso(s.curso)}</b> <span className="chip warn">Pendiente</span>
                        <p>
                          {s.estudiante} · {s.horas} h · {colones(s.total)}
                        </p>
                        {(s.detalle || "").split("<br>").map((linea, i) => (
                          <p key={i}>{linea}</p>
                        ))}
                        {s.nota && (
                          <p className="fila-nota"><b>Nota del estudiante:</b> {s.nota}</p>
                        )}
                      </div>
                      <div className="fila-acciones">
                        <button className="btn btn-p" disabled={!!respondiendoSolicitud}
                          onClick={() => confirmarSolicitud(s)}>
                          {ocupado ? "Enviando…" : "Puedo dar la clase"}
                        </button>
                        <button className="btn-peligro-ghost" disabled={!!respondiendoSolicitud}
                          onClick={() => setRechazando(s)}>
                          No puedo dar la clase
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mini-card dash-sec">
              <h2>Próximas reuniones</h2>
              {!datos.proximasReuniones.length ? (
                <p className="vacio">No tiene reuniones pagadas por venir.</p>
              ) : datos.proximasReuniones.map((r) => {
                const enCurso = r.fin && new Date() >= new Date(r.inicio) && new Date() < new Date(r.fin);
                return (
                <div className="fila" key={r.token}>
                  <div className="fila-info">
                    <b>{nombreCurso(r.curso)}</b>{" "}
                    <span className={`chip ${enCurso ? "success" : "warn"}`}>
                      {enCurso ? "En curso" : "Próxima"}
                    </span>
                    <p>{r.estudiante} · {fechaHora(r.inicio)}</p>
                  </div>
                  <div className="fila-acciones">
                    {r.sala && (
                      <a className="btn btn-p" href={r.sala} target="_blank" rel="noopener noreferrer">
                        Unirse a la reunión
                      </a>
                    )}
                    <button className="btn btn-danger" onClick={() => setCancelando(r)}>
                      Cancelar
                    </button>
                  </div>
                </div>
                );
              })}

              <div className="historial-toggle">
                <button className="btn"
                  onClick={() => { setPaginaHistorial(1); setMostrarHistorial((v) => !v); }}>
                  {mostrarHistorial ? "Ocultar historial" : "Ver historial de reuniones"}
                </button>
              </div>
              {mostrarHistorial && (
                <div className="historial-lista">
                  <h3>Historial de reuniones</h3>
                  {!historial.length ? (
                    <p className="vacio">Todavía no tiene reuniones pasadas.</p>
                  ) : (
                    <>
                      {historialVisible.map((r) => (
                        <div className="fila" key={r.token}>
                          <div className="fila-info">
                            <b>{nombreCurso(r.curso)}</b>
                            <p>
                              {r.estudiante} · {fechaHora(r.inicio)}
                              {r.calificacion ? ` · ${r.calificacion}★` : ""}
                            </p>
                          </div>
                          <span className="chip off">Completada</span>
                        </div>
                      ))}
                      {paginasHistorial > 1 && (
                        <nav className="pag" aria-label="Páginas del historial de reuniones">
                          <button type="button" className="pbtn" disabled={paginaHistorialSegura === 1}
                            onClick={() => setPaginaHistorial(paginaHistorialSegura - 1)}
                            aria-label="Página anterior">‹</button>
                          {Array.from({ length: paginasHistorial }, (_, i) => i + 1).map((p) => (
                            <button type="button" key={p}
                              className={`pbtn ${p === paginaHistorialSegura ? "on" : ""}`}
                              aria-current={p === paginaHistorialSegura ? "page" : undefined}
                              aria-label={`Página ${p}`}
                              onClick={() => setPaginaHistorial(p)}>{p}</button>
                          ))}
                          <button type="button" className="pbtn"
                            disabled={paginaHistorialSegura === paginasHistorial}
                            onClick={() => setPaginaHistorial(paginaHistorialSegura + 1)}
                            aria-label="Página siguiente">›</button>
                        </nav>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="mini-card dash-sec">
              <h2>Reseñas de estudiantes</h2>
              {!resenas.length ? (
                <p className="vacio">Todavía no tiene reseñas de estudiantes.</p>
              ) : (
                <>
                  {resenasVisibles.map((r, i) => (
                    <div className="fila" key={i}>
                      <div className="fila-info">
                        <b>{nombreCurso(r.curso)}</b>{" "}
                        <span className="rank-rating">
                          <b>{"★".repeat(r.calificacion)}</b>{"☆".repeat(5 - r.calificacion)}
                        </span>
                        <p>
                          {r.estudiante} · {fechaHora(r.fecha)}
                          {r.repetiria != null && (r.repetiria ? " · Repetiría" : " · No repetiría")}
                          {r.puntual != null && (r.puntual ? " · Puntual" : " · No fue puntual")}
                        </p>
                        {r.comentario && <p className="fila-nota"><b>Comentario:</b> {r.comentario}</p>}
                      </div>
                    </div>
                  ))}
                  {paginasResenas > 1 && (
                    <nav className="pag" aria-label="Páginas de reseñas de estudiantes">
                      <button type="button" className="pbtn" disabled={paginaResenasSegura === 1}
                        onClick={() => setPaginaResenas(paginaResenasSegura - 1)}
                        aria-label="Página anterior">‹</button>
                      {Array.from({ length: paginasResenas }, (_, i) => i + 1).map((p) => (
                        <button type="button" key={p}
                          className={`pbtn ${p === paginaResenasSegura ? "on" : ""}`}
                          aria-current={p === paginaResenasSegura ? "page" : undefined}
                          aria-label={`Página ${p}`}
                          onClick={() => setPaginaResenas(p)}>{p}</button>
                      ))}
                      <button type="button" className="pbtn"
                        disabled={paginaResenasSegura === paginasResenas}
                        onClick={() => setPaginaResenas(paginaResenasSegura + 1)}
                        aria-label="Página siguiente">›</button>
                    </nav>
                  )}
                </>
              )}
            </div>

            <div className="mini-card dash-sec">
              <h2>Historial de pagos</h2>
              <p className="ayuda" style={{ margin: "0 0 6px" }}>
                Ciclos ya transferidos a su cuenta bancaria. El ciclo en curso y lo que todavía
                está pendiente de depósito se ven arriba, en las tarjetas de resumen.
              </p>
              {!datos.historialPagos?.length ? (
                <p className="vacio">Todavía no tiene pagos registrados.</p>
              ) : datos.historialPagos.map((p) => (
                <div className="fila" key={p.semana}>
                  <div className="fila-info">
                    <b>Ciclo {p.rango}</b>
                    <p>
                      {p.sesiones} {p.sesiones === 1 ? "sesión" : "sesiones"}
                      {p.pagadoEn ? ` · Pagado el ${fechaHora(p.pagadoEn)}` : ""}
                    </p>
                  </div>
                  <span className="chip success">{colones(p.monto)}</span>
                </div>
              ))}
            </div>

            <div className="mini-card dash-sec">
              <h2>Incidentes y reportes</h2>
              <p className="ayuda" style={{ margin: "0 0 6px" }}>
                Registro rápido de una queja, una falla técnica u otro problema puntual de una
                sesión, para que el equipo le dé seguimiento. Su nombre ya va incluido, no hace
                falta que lo escriba.
              </p>
              {errorIncidente && <div className="error" role="alert" style={{ marginBottom: 14 }}>{errorIncidente}</div>}

              <div style={{ display: "flex", gap: "12px 16px", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 200px" }}>
                  <label className="lb" htmlFor="inc-curso" style={{ marginTop: 0 }}>Curso (opcional)</label>
                  <select id="inc-curso" className="in" value={formIncidente.curso}
                    onChange={(e) => setFormIncidente((f) => ({ ...f, curso: e.target.value }))}>
                    <option value="">—</option>
                    {datos.cursos.map((c) => (
                      <option key={c.curso} value={c.curso}>{nombreCurso(c.curso)}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: "1 1 200px" }}>
                  <label className="lb" htmlFor="inc-estudiante" style={{ marginTop: 0 }}>
                    Estudiante (opcional)
                  </label>
                  <input id="inc-estudiante" className="in" value={formIncidente.estudiante}
                    onChange={(e) => setFormIncidente((f) => ({ ...f, estudiante: e.target.value }))} />
                </div>
                <div style={{ flex: "1 1 200px" }}>
                  <label className="lb" htmlFor="inc-tipo" style={{ marginTop: 0 }}>Tipo</label>
                  <select id="inc-tipo" className="in" value={formIncidente.tipo}
                    onChange={(e) => setFormIncidente((f) => ({ ...f, tipo: e.target.value }))}>
                    <option value="queja">Queja de estudiante</option>
                    <option value="tecnico">Problema técnico</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              </div>
              <label className="lb" htmlFor="inc-descripcion">Qué pasó</label>
              <textarea id="inc-descripcion" className="in ta" rows={3} maxLength={500}
                value={formIncidente.descripcion}
                placeholder="Describa brevemente el incidente, con la fecha de la sesión si aplica."
                onChange={(e) => setFormIncidente((f) => ({ ...f, descripcion: e.target.value }))} />
              <button className="btn btn-p" style={{ marginTop: 14 }}
                disabled={creandoIncidente} onClick={reportarIncidente}>
                {creandoIncidente ? "Guardando…" : "Reportar incidente"}
              </button>
            </div>
          </>
        )}
      </div>

      {editando && (
        <EditarCurso
          curso={editando}
          sessionToken={sesion.token}
          onCerrado={() => setEditando(null)}
          onGuardado={async () => {
            setEditando(null); await cargar(); mostrarExito("Curso actualizado.");
          }}
          onSesionMuerta={() => irALogin("Su sesión venció. Inicie sesión de nuevo.")}
        />
      )}

      {editandoHorario && datos && (
        <EditarHorario
          disponibilidad={datos.disponibilidad || {}}
          sessionToken={sesion.token}
          onCerrado={() => setEditandoHorario(false)}
          onGuardado={async () => {
            setEditandoHorario(false); await cargar(); mostrarExito("Horario de disponibilidad actualizado.");
          }}
          onSesionMuerta={() => irALogin("Su sesión venció. Inicie sesión de nuevo.")}
        />
      )}

      {agregandoCurso && datos && (
        <AgregarCurso
          cursosExistentes={datos.cursos.map((c) => c.curso)}
          sessionToken={sesion.token}
          onCerrado={() => setAgregandoCurso(false)}
          onGuardado={async () => {
            setAgregandoCurso(false); await cargar();
            mostrarExito("Curso agregado. Le avisamos por correo cuando quede aprobado.");
          }}
          onSesionMuerta={() => irALogin("Su sesión venció. Inicie sesión de nuevo.")}
        />
      )}

      {cancelando && (
        <CancelarSesion
          titulo="Cancelar esta tutoría"
          resumen={`${nombreCurso(cancelando.curso)} con ${cancelando.estudiante}, `
            + `${fechaHora(cancelando.inicio)}`}
          aviso={"Al cancelarla usted, al estudiante se le devuelve el 100% de lo que pagó. "
            + "Le avisamos a él por correo con el motivo que escriba acá, y al equipo para que "
            + "haga la devolución."}
          onCancelar={cancelarSesion}
          onCerrar={() => setCancelando(null)}
          onListo={async () => {
            setCancelando(null); await cargar();
            mostrarExito("Tutoría cancelada. Le avisamos al estudiante por correo.");
          }}
        />
      )}

      {rechazando && (
        <RechazarSolicitud
          resumen={`${nombreCurso(rechazando.curso)} con ${rechazando.estudiante}, `
            + `${rechazando.horas} h.`}
          onRechazar={rechazarSolicitud}
          onCerrar={() => setRechazando(null)}
          onListo={async () => {
            setRechazando(null); await cargar();
            mostrarExito("Anotado. Le mandamos su mensaje al estudiante por correo.");
          }}
        />
      )}

      {cambiandoPassword && (
        <CambiarPassword
          sessionToken={sesion.token}
          onCerrado={() => setCambiandoPassword(false)}
          onGuardado={() => {
            setCambiandoPassword(false); mostrarExito("Contraseña actualizada.");
          }}
          onSesionMuerta={() => irALogin("Su sesión venció. Inicie sesión de nuevo.")}
        />
      )}

      {cambiandoCorreo && (
        <CambiarCorreoTutor
          sessionToken={sesion.token}
          correoActual={datos?.correo}
          onCerrado={() => setCambiandoCorreo(false)}
          onGuardado={(nuevoCorreo) => {
            setCambiandoCorreo(false);
            setDatos((d) => (d ? { ...d, correo: nuevoCorreo } : d));
            mostrarExito("Correo actualizado.");
          }}
          onSesionMuerta={() => irALogin("Su sesión venció. Inicie sesión de nuevo.")}
        />
      )}

      {viendoResenasCurso && (
        <ResenasCurso
          nombreCurso={nombreCurso(viendoResenasCurso)}
          resenas={(datos?.resenas || []).filter((r) => r.curso === viendoResenasCurso)}
          onCerrado={() => setViendoResenasCurso(null)}
        />
      )}

      <Accesibilidad ajustes={a11y} setAjustes={setA11y} />
    </div>
  );
}

function CambiarPassword({ sessionToken, onCerrado, onGuardado, onSesionMuerta }) {
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const validar = () => {
    if (!actual) return "Ingrese su contraseña actual.";
    if (nueva.length < 8) return "La contraseña nueva debe tener al menos 8 caracteres.";
    if (nueva !== confirmar) return "Las contraseñas nuevas no coinciden.";
    return "";
  };

  const guardar = async () => {
    const mensaje = validar();
    if (mensaje) { setError(mensaje); return; }
    setGuardando(true); setError("");
    try {
      await api.cambiarPasswordTutor(sessionToken, actual, nueva);
      onGuardado();
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return onSesionMuerta();
      if (codigo === "PASSWORD_ACTUAL_INCORRECTA") setError("La contraseña actual no es correcta.");
      else setError(err.message || "No pudimos cambiar su contraseña.");
    } finally {
      setGuardando(false);
    }
  };

  const hayCambios = !!(actual || nueva || confirmar);

  return (
    <Capa onCerrar={onCerrado} etiqueta="Cambiar contraseña" confirmarSalida={hayCambios}>
      <div className="mdl">
        <div className="mdl-h">
          <h3>Cambiar contraseña</h3>
          <button className="x" aria-label="Cerrar" onClick={onCerrado}>×</button>
        </div>
        <div className="mdl-b">
          <label className="lb" htmlFor="pw-actual">Contraseña actual</label>
          <CampoPassword id="pw-actual" className="in" value={actual}
            autoComplete="current-password" onChange={(e) => setActual(e.target.value)} />

          <label className="lb" htmlFor="pw-nueva">Contraseña nueva</label>
          <CampoPassword id="pw-nueva" className="in" value={nueva}
            autoComplete="new-password" onChange={(e) => setNueva(e.target.value)} />

          <label className="lb" htmlFor="pw-confirmar">Confirmar contraseña nueva</label>
          <CampoPassword id="pw-confirmar" className="in" value={confirmar}
            autoComplete="new-password" onChange={(e) => setConfirmar(e.target.value)} />

          {error && <div className="error" role="alert">{error}</div>}

          <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
            disabled={guardando} onClick={guardar}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </Capa>
  );
}

/* Mismo patrón que CambiarPassword: exige la contraseña actual. A
   diferencia del correo del estudiante, acá no hace falta migrar ninguna
   otra hoja — el correo del tutor no es la llave de nada (ver
   cambiarCorreoTutor en apps-script.gs), así que el cambio es directo. */
function CambiarCorreoTutor({ sessionToken, correoActual, onCerrado, onGuardado, onSesionMuerta }) {
  const [password, setPassword] = useState("");
  const [nuevoCorreo, setNuevoCorreo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const validar = () => {
    if (!password) return "Ingrese su contraseña actual.";
    if (!nuevoCorreo.trim().includes("@")) return "Ingrese un correo válido.";
    return "";
  };

  const guardar = async () => {
    const mensaje = validar();
    if (mensaje) { setError(mensaje); return; }
    setGuardando(true); setError("");
    try {
      const r = await api.cambiarCorreoTutor(sessionToken, password, nuevoCorreo.trim());
      onGuardado(r.correo);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return onSesionMuerta();
      if (codigo === "PASSWORD_ACTUAL_INCORRECTA") setError("La contraseña actual no es correcta.");
      else if (codigo === "CORREO_OCUPADO") setError("Ya hay otra cuenta de tutor con ese correo.");
      else if (codigo === "CORREO_INVALIDO") setError("Ingrese un correo válido.");
      else if (codigo === "CORREO_IGUAL") setError("Ese ya es su correo actual.");
      else setError(err.message || "No pudimos cambiar su correo.");
    } finally {
      setGuardando(false);
    }
  };

  const hayCambios = !!(password || nuevoCorreo);

  return (
    <Capa onCerrar={onCerrado} etiqueta="Cambiar correo" confirmarSalida={hayCambios}>
      <div className="mdl">
        <div className="mdl-h">
          <h3>Cambiar correo</h3>
          <button className="x" aria-label="Cerrar" onClick={onCerrado}>×</button>
        </div>
        <div className="mdl-b">
          {correoActual && (
            <p className="ayuda" style={{ marginTop: 0 }}>Correo actual: <b>{correoActual}</b></p>
          )}

          <label className="lb" htmlFor="correo-nuevo-tutor">Correo nuevo</label>
          <input id="correo-nuevo-tutor" type="email" className="in" value={nuevoCorreo}
            autoComplete="email" onChange={(e) => setNuevoCorreo(e.target.value)} />

          <label className="lb" htmlFor="correo-password-tutor">Contraseña actual</label>
          <CampoPassword id="correo-password-tutor" className="in" value={password}
            autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} />

          {error && <div className="error" role="alert">{error}</div>}

          <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
            disabled={guardando} onClick={guardar}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </Capa>
  );
}

const DESCRIPCION_MINIMA = 20;

/* Descripción y tarifa del curso, un solo modal: la tarifa muestra el mismo
   desglose en vivo (paga el estudiante / recibe usted) que ya tiene
   AgregarCurso, para que cambiar el precio nunca sea un número a ciegas.
   Cada campo se manda a su propio endpoint (editarDescripcionCurso /
   cambiarTarifaCurso — la hoja no tiene una escritura combinada) y solo el
   que de verdad cambió, para no reescribir una columna sin necesidad. */
function EditarCurso({ curso, sessionToken, onCerrado, onGuardado, onSesionMuerta }) {
  const [texto, setTexto] = useState(curso.descripcion || "");
  const [tarifa, setTarifa] = useState(String(curso.tarifaBase ?? ""));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const corta = texto.trim().length < DESCRIPCION_MINIMA;
  const tarifaInvalida = !(Number(tarifa) >= TARIFA_MINIMA);
  const pr = precioDe(tarifa);
  const hayCambios = texto.trim() !== (curso.descripcion || "").trim()
    || Number(tarifa) !== Number(curso.tarifaBase || 0);

  const guardar = async () => {
    if (corta) { setError(`Escriba al menos ${DESCRIPCION_MINIMA} caracteres.`); return; }
    if (tarifaInvalida) { setError(`La tarifa mínima es ${colones(TARIFA_MINIMA)}.`); return; }
    setGuardando(true); setError("");
    try {
      const cambios = [];
      if (texto.trim() !== (curso.descripcion || "").trim()) {
        cambios.push(api.editarDescripcionCurso(sessionToken, curso.curso, texto.trim()));
      }
      if (Number(tarifa) !== Number(curso.tarifaBase || 0)) {
        cambios.push(api.cambiarTarifaCurso(sessionToken, curso.curso, Number(tarifa)));
      }
      await Promise.all(cambios);
      await onGuardado();
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return onSesionMuerta();
      setError(err.message || "No pudimos guardar el cambio.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Capa onCerrar={onCerrado} etiqueta={`Editar ${curso.curso}`} confirmarSalida={hayCambios}>
      <div className="mdl">
        <div className="mdl-h">
          <h3>Editar {curso.curso}</h3>
          <button className="x" aria-label="Cerrar" onClick={onCerrado}>×</button>
        </div>
        <div className="mdl-b">
          <label className="lb" htmlFor="tarifa-curso" style={{ marginTop: 0 }}>Cobra por hora</label>
          <input id="tarifa-curso" type="number" min={TARIFA_MINIMA} step="100"
            className={`in ${tarifaInvalida ? "falta" : ""}`} aria-invalid={tarifaInvalida}
            placeholder={`₡${TARIFA_SUGERIDA}`} value={tarifa}
            onChange={(e) => setTarifa(e.target.value)} />
          {tarifaInvalida ? (
            <p className="ayuda mal" role="alert" style={{ margin: "4px 0 0" }}>
              El mínimo son ₡3000
            </p>
          ) : (
            <p className="ayuda" style={{ margin: "4px 0 0" }}>
              El estudiante paga <b>{colones(pr.cliente)}</b> por hora · usted recibe{" "}
              <b>{colones(pr.tutor)}</b> por hora.
            </p>
          )}

          <label className="lb" htmlFor="descripcion">Descripción</label>
          <textarea id="descripcion" className="in ta" rows={4} maxLength={400} value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Cuénteles a los estudiantes cómo da sus tutorías." />
          <p className="ayuda" style={{ margin: "4px 0 0" }}>
            {corta
              ? `${texto.trim().length} de ${DESCRIPCION_MINIMA} caracteres mínimo`
              : `${texto.length}/400`}
          </p>

          {error && <div className="error" role="alert">{error}</div>}

          <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
            disabled={guardando} onClick={guardar}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </Capa>
  );
}

const POR_PAGINA_RESENAS_CURSO = 10;

function ResenasCurso({ nombreCurso, resenas, onCerrado }) {
  const [pagina, setPagina] = useState(1);
  const paginas = Math.max(1, Math.ceil(resenas.length / POR_PAGINA_RESENAS_CURSO));
  const paginaSegura = Math.min(pagina, paginas);
  const visibles = resenas.slice(
    (paginaSegura - 1) * POR_PAGINA_RESENAS_CURSO,
    paginaSegura * POR_PAGINA_RESENAS_CURSO
  );

  return (
    <Capa onCerrar={onCerrado} etiqueta={`Reseñas de ${nombreCurso}`}>
      <div className="mdl mdl-ancho">
        <div className="mdl-h">
          <h3>Reseñas de {nombreCurso}</h3>
          <button className="x" aria-label="Cerrar" onClick={onCerrado}>×</button>
        </div>
        <div className="mdl-b">
          {!resenas.length ? (
            <p className="vacio">Todavía no tiene reseñas para este curso.</p>
          ) : (
            <>
              {visibles.map((r, i) => (
                <div className="fila" key={i}>
                  <div className="fila-info">
                    <span className="rank-rating">
                      <b>{"★".repeat(r.calificacion)}</b>{"☆".repeat(5 - r.calificacion)}
                    </span>
                    <p>
                      {r.estudiante} · {fechaHora(r.fecha)}
                      {r.repetiria != null && (r.repetiria ? " · Repetiría" : " · No repetiría")}
                      {r.puntual != null && (r.puntual ? " · Puntual" : " · No fue puntual")}
                    </p>
                    {r.comentario && <p className="fila-nota"><b>Comentario:</b> {r.comentario}</p>}
                  </div>
                </div>
              ))}
              {paginas > 1 && (
                <nav className="pag" aria-label={`Páginas de reseñas de ${nombreCurso}`}>
                  <button type="button" className="pbtn" disabled={paginaSegura === 1}
                    onClick={() => setPagina(paginaSegura - 1)} aria-label="Página anterior">‹</button>
                  {Array.from({ length: paginas }, (_, i) => i + 1).map((p) => (
                    <button type="button" key={p} className={`pbtn ${p === paginaSegura ? "on" : ""}`}
                      aria-current={p === paginaSegura ? "page" : undefined}
                      aria-label={`Página ${p}`} onClick={() => setPagina(p)}>{p}</button>
                  ))}
                  <button type="button" className="pbtn" disabled={paginaSegura === paginas}
                    onClick={() => setPagina(paginaSegura + 1)} aria-label="Página siguiente">›</button>
                </nav>
              )}
            </>
          )}
        </div>
      </div>
    </Capa>
  );
}

function EditarHorario({ disponibilidad, sessionToken, onCerrado, onGuardado, onSesionMuerta }) {
  const [disp, setDisp] = useState(disponibilidad);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const marcados = Object.keys(disp).length;

  const guardar = async () => {
    setGuardando(true); setError("");
    try {
      await api.cambiarDisponibilidadTutor(sessionToken, disp);
      await onGuardado();
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return onSesionMuerta();
      setError(err.message || "No pudimos guardar el cambio.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Capa onCerrar={onCerrado} etiqueta="Modificar horario de disponibilidad"
      confirmarSalida={JSON.stringify(disp) !== JSON.stringify(disponibilidad)}>
      <div className="mdl mdl-ancho">
        <div className="mdl-h">
          <h3>Horario de disponibilidad</h3>
          <button className="x" aria-label="Cerrar" onClick={onCerrado}>×</button>
        </div>
        <div className="mdl-b">
          <div className="notas">
            <p>Toque un bloque para <b>marcarlo</b> disponible; tóquelo de nuevo para dejarlo en <b>no disponible</b>.</p>
            <p>Lo que marque vale para <b>todo el semestre</b>. <b>Un administrador confirma día y hora</b> de
              cada sesión sobre esa base.</p>
          </div>
          <div className="displb">
            <label className="lb" style={{ margin: 0 }}>
              {marcados} bloque{marcados === 1 ? "" : "s"} marcado{marcados === 1 ? "" : "s"}
            </label>
          </div>
          <Grilla valor={disp} onChange={setDisp} />

          {error && <div className="error" role="alert">{error}</div>}

          <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
            disabled={guardando} onClick={guardar}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </Capa>
  );
}

const AREAS_SELECCIONABLES = AREAS.filter((a) => a.cursos.length > 0);

/* Las áreas de la UCR (53) y las de Software (17) traen un `grupo` — el área
   académica de la universidad, la familia de la herramienta — que acá se
   vuelve <optgroup>. Sin encabezados, dar con "Ingeniería Topográfica" entre
   cincuenta y tres opciones sueltas es buscar a ciegas. Las instituciones sin
   grupo (Tec, Idiomas, Colegios, Admisión) tienen pocas áreas y siguen yendo
   planas. Se agrupa por corridas consecutivas, no juntando por nombre: el
   orden del catálogo YA viene por grupo y es el que hay que respetar. */
const porGrupo = (areas) => areas.reduce((out, a) => {
  const ultimo = out[out.length - 1];
  if (ultimo && ultimo.grupo === a.grupo) ultimo.areas.push(a);
  else out.push({ grupo: a.grupo, areas: [a] });
  return out;
}, []);

/* Versión corta del bloque por-curso de FormTutor.jsx (registro inicial):
   nombre, carrera, año, foto y disponibilidad ya están cargados (se
   reutilizan del lado del servidor por idTutor, ver agregarCursoTutor), así
   que este formulario solo pide lo propio de un curso nuevo. Tres <select>
   en cascada (institución → área → curso) en vez de reusar BuscadorCursos:
   ese componente está pensado para elegir VARIOS cursos a la vez con
   autocompletar y chips, y solo tiene estilos en estilos/global.js (no en
   minimos.js, que es lo que carga esta página) — para un único curso, tres
   selects sobre INSTITUCIONES/AREAS ya cubre lo mismo con cero CSS nuevo,
   con la misma jerarquía Tec/Idiomas/Colegios que usa el registro inicial. */
function AgregarCurso({ cursosExistentes, sessionToken, onCerrado, onGuardado, onSesionMuerta }) {
  /* En el orden que declara la institución, que es el que trae los `grupo`
     seguidos; filtrar AREAS devolvía el orden del archivo y rompía los
     encabezados. */
  const areasDeInstitucion = (id) => (INSTITUCIONES.find((i) => i.id === id)?.areas || [])
    .map((a) => AREAS_SELECCIONABLES.find((x) => x.id === a))
    .filter(Boolean);
  const cursoUnicoDeArea = (id) => {
    const cursos = AREAS_SELECCIONABLES.find((a) => a.id === id)?.cursos || [];
    return cursos.length === 1 ? cursos[0].cod : "";
  };
  const [institucion, setInstitucion] = useState(INSTITUCIONES[0]?.id || "");
  const [area, setArea] = useState(areasDeInstitucion(INSTITUCIONES[0]?.id || "")[0]?.id || "");
  const [curso, setCurso] = useState(() => cursoUnicoDeArea(areasDeInstitucion(INSTITUCIONES[0]?.id || "")[0]?.id || ""));
  const [nota, setNota] = useState("");
  const [tarifaBase, setTarifaBase] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [comprobante, setComprobante] = useState(null);
  const [preparandoComp, setPreparandoComp] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [errorArchivo, setErrorArchivo] = useState("");
  const compTokenRef = useRef(0);

  /* Qué se le pide a quien agrega este curso: un nivel de una lista
     (Idiomas, Software) o un número dentro de un rango (80–100 de
     expediente, 0–900 del examen de admisión). Sale de la institución
     elegida arriba (ver `verificacion` en datos/areas.js y ESCALAS_NIVEL en
     lib/horarios.js), no de una lista de ids ni de un rango escrito acá:
     es la misma escala, con la misma copia, que usa el registro inicial. */
  const escala = escalaDeInstitucion(INSTITUCIONES.find((i) => i.id === institucion));
  const pideNivel = esDeOpciones(escala);
  /* Colegios no pide respaldo: su escala no lleva ni nota ni comprobante
     (ver "colegio" en ESCALAS_NIVEL, lib/horarios.js), así que los dos
     campos no se dibujan ni se validan. */
  const pideRespaldoAqui = pideRespaldo(escala);
  const notaFuera = fueraDeEscala(escala, nota);
  const areasDelInstitucion = areasDeInstitucion(institucion);
  const cursosDelArea = AREAS_SELECCIONABLES.find((a) => a.id === area)?.cursos || [];
  const pr = precioDe(tarifaBase);
  const tarifaBaja = tarifaBase !== "" && Number(tarifaBase) < TARIFA_MINIMA;
  const hayCambios = !!(curso || nota || comprobante || tarifaBase || descripcion);
  /* La escala manda: al cambiar de institución hacia o desde Colegios, lo
     que ya se había llenado deja de aplicar y se descarta con ella. */
  useEffect(() => {
    if (pideRespaldoAqui) return;
    setNota(""); setComprobante(null); setErrorArchivo("");
  }, [pideRespaldoAqui]);

  const elegirComprobante = async (archivo) => {
    try {
      validarExtensionImagen(archivo);
    } catch (err) {
      setErrorArchivo(err.message);
      return;
    }
    const miToken = ++compTokenRef.current;
    setPreparandoComp(true); setErrorArchivo("");
    try {
      const lista = await prepararComprobante(archivo);
      if (compTokenRef.current === miToken) setComprobante(lista);
    } catch (err) {
      if (compTokenRef.current === miToken) setErrorArchivo(err.message);
    } finally {
      if (compTokenRef.current === miToken) setPreparandoComp(false);
    }
  };

  const validar = () => {
    if (!curso) return "Elija un curso.";
    if (cursosExistentes.includes(curso)) return "Ya tiene ese curso cargado.";
    if (!valorDeEscalaOk(escala, nota)) {
      return pideNivel ? `Elija ${escala.deQue}.` : escala.fuera;
    }
    if (pideRespaldoAqui && !comprobante) return "Suba el comprobante.";
    if (!(Number(tarifaBase) >= TARIFA_MINIMA)) return `La tarifa mínima es ${colones(TARIFA_MINIMA)}.`;
    if (descripcion.trim().length < 20) return "La descripción debe tener al menos 20 caracteres.";
    return "";
  };

  const guardar = async () => {
    const mensaje = validar();
    if (mensaje) { setError(mensaje); return; }
    setGuardando(true); setError("");
    try {
      await api.agregarCursoTutor(sessionToken, curso, nota, comprobante, tarifaBase, descripcion.trim());
      await onGuardado();
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (CODIGOS_SESION_MUERTA.includes(codigo)) return onSesionMuerta();
      setError(err.message || "No pudimos agregar el curso.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Capa onCerrar={onCerrado} etiqueta="Agregar nuevo curso" confirmarSalida={hayCambios}>
      <div className="mdl mdl-ancho">
        <div className="mdl-h">
          <h3>Agregar nuevo curso</h3>
          <button className="x" aria-label="Cerrar" onClick={onCerrado}>×</button>
        </div>
        <div className="mdl-b">
          <p style={{ marginBottom: 16 }}>
            Su nombre, carrera, foto y horario de disponibilidad ya cargados se usan igual para
            este curso — solo hace falta <b>lo propio de él</b>.
          </p>

          <label className="lb" htmlFor="institucion-nueva">Institución</label>
          <select id="institucion-nueva" className="in" value={institucion}
            onChange={(e) => {
              const nuevaInstitucion = e.target.value;
              const nuevaArea = areasDeInstitucion(nuevaInstitucion)[0]?.id || "";
              setInstitucion(nuevaInstitucion);
              setArea(nuevaArea);
              setCurso(cursoUnicoDeArea(nuevaArea));
              setNota("");   // una nota de 92 no es un nivel, ni un B2 una nota
            }}>
            {INSTITUCIONES.map((i) => <option key={i.id} value={i.id}>{i.nombre}</option>)}
          </select>

          <label className="lb" htmlFor="area-nueva">Área</label>
          <select id="area-nueva" className="in" value={area}
            onChange={(e) => { setArea(e.target.value); setCurso(cursoUnicoDeArea(e.target.value)); }}>
            {porGrupo(areasDelInstitucion).map(({ grupo, areas }) => (grupo ? (
              <optgroup key={grupo} label={grupo}>
                {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </optgroup>
            ) : (
              areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)
            )))}
          </select>

          {cursosDelArea.length !== 1 && (
            <>
              <label className="lb" htmlFor="curso-nuevo">Curso</label>
              <select id="curso-nuevo" className="in" value={curso} onChange={(e) => setCurso(e.target.value)}>
                <option value="">Elija un curso</option>
                {/* Con código adelante, igual que los chips del buscador público: en la
                    UCR hay decenas de "Seminario" y de "Cálculo…", y el nombre solo no
                    alcanza para saber cuál se está eligiendo. */}
                {cursosDelArea.map((c) => (
                  <option key={c.cod} value={c.cod}>
                    {c.sinCodigo ? c.nom : `${c.cod} · ${c.nom}`}
                  </option>
                ))}
              </select>
            </>
          )}

          {pideRespaldoAqui ? (
          <>
          <label className="lb" htmlFor="nota-nueva">{escala.etiqueta}</label>
          {pideNivel ? (
            <select id="nota-nueva" className="in" value={nota} onChange={(e) => setNota(e.target.value)}>
              <option value="">Elija</option>
              {escala.niveles.map((n) => <option key={n.id} value={n.id}>{n.id} · {n.d}</option>)}
            </select>
          ) : (
            /* Solo dígitos, y el aviso al toque si el número se sale del
               rango — mismo campo y mismas reglas que el registro inicial
               (ver FormTutor.jsx), incluido el porqué de type="text". */
            <input id="nota-nueva" type="text" inputMode="numeric" autoComplete="off"
              className={`in ${notaFuera ? "falta" : ""}`} aria-invalid={notaFuera}
              placeholder={`${escala.min}–${escala.max}`}
              value={nota} onChange={(e) => setNota(soloDigitos(e.target.value, escala))} />
          )}
          {notaFuera && (
            <p className="ayuda mal" role="alert" style={{ margin: "8px 0 0" }}>{escala.fuera}</p>
          )}

          <label className="lb" style={{ margin: "20px 0 10px" }}>
            Comprobante de {escala.deQue}
          </label>
          <AlertaArchivo mensaje={errorArchivo} />
          <div className="fotorow">
            <div className="fotoprev">
              {comprobante ? <img src={comprobante} alt="Vista previa del comprobante" /> : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"
                    fill="none" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              )}
            </div>
            <div className="fotoacc">
              <label className={`btn fotobtn ${preparandoComp ? "ocupado" : ""}`}>
                {preparandoComp ? "Preparando…" : comprobante ? "Cambiar" : "Subir comprobante"}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) elegirComprobante(f);
                }} />
              </label>
              <p className="ayuda" style={{ margin: "8px 0 0" }}>
                {escala.comprobante} Formatos permitidos: <b>{FORMATOS_IMAGEN_TEXTO}</b>.
              </p>
            </div>
          </div>
          </>
          ) : (
            <p className="privado" style={{ marginTop: 20 }}>
              Para materias de colegio <b>no pedimos nota ni comprobante</b>. El curso entra
              pendiente igual, y un administrador lo revisa antes de publicarlo.
            </p>
          )}

          <label className="lb" htmlFor="tarifa-nueva">Cobra por hora</label>
          <input id="tarifa-nueva" type="number" min={TARIFA_MINIMA} step="100"
            className={`in ${tarifaBaja ? "falta" : ""}`} aria-invalid={tarifaBaja}
            placeholder={`₡${TARIFA_SUGERIDA}`} value={tarifaBase}
            onChange={(e) => setTarifaBase(e.target.value)} />
          {tarifaBaja ? (
            <p className="ayuda mal" role="alert" style={{ margin: "8px 0 0" }}>
              El mínimo son ₡3000
            </p>
          ) : Number(tarifaBase) >= TARIFA_MINIMA && (
            <p style={{ margin: "8px 0 0", fontSize: 14, color: "var(--text-3)" }}>
              El estudiante paga <b>{colones(pr.cliente)}</b> · usted recibe <b>{colones(pr.tutor)}</b>
            </p>
          )}

          <label className="lb" htmlFor="descripcion-nueva">Descripción</label>
          <textarea id="descripcion-nueva" className="in ta" rows={3} maxLength={180} value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Cuénteles a los estudiantes qué cubre en este curso." />
          <p className="ayuda" style={{ margin: "4px 0 0" }}>
            {descripcion.trim().length < 20
              ? `${descripcion.trim().length} de 20 caracteres mínimo`
              : `${descripcion.length}/180`}
          </p>

          {error && <div className="error" role="alert">{error}</div>}

          <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
            disabled={guardando} onClick={guardar}>
            {guardando ? "Agregando…" : "Agregar curso"}
          </button>
        </div>
      </div>
    </Capa>
  );
}
